import moment from 'moment';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import { formStyles } from '../../styles/forms';
import { DatePickerSelector } from '../forms';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { useRoleHex } from '../../themes/useRoleHex';

type QuickReminderFieldProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
};

export const QuickReminderField = ({
  control,
  setValue,
}: QuickReminderFieldProps) => {
  const { t } = useTranslation();
  const reminderDate = useWatch({ control, name: 'quickReminderDate' });
  // Placeholder grey for the dateless time field — role-based so it tracks
  // the active theme rather than pinning one mode's hex.
  const placeholderHex = useRoleHex('textDisabled');

  return (
    <>
      <View>
        <Text style={formStyles.formItemTitleCropCircle}>
          {t('issue.quickReminder.header')}
        </Text>
      </View>
      <Text style={styles.subheader}>{t('issue.quickReminder.subheader')}</Text>
      {/* Date + time two-up, matching the blackout-dates From/To row. The time
          is always rendered for discoverability but disabled until a date
          exists: without one no reminder is created, so an editable time
          would be a promise the form can't keep. */}
      <View style={styles.row}>
        <DatePickerSelector
          name="quickReminderDate"
          setValue={setValue}
          control={control}
          label={t('issue.reminderDate')}
          placeholder={t('issue.dateFormat')}
          maxYears={5}
          minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
          hideCalendarIcon
        />
        <DatePickerSelector
          mode="time"
          name="quickReminderTime"
          setValue={setValue}
          control={control}
          label={t('issue.reminderTime')}
          placeholder="00:00"
          containerStyle={styles.timeField}
          disabled={!reminderDate}
          disabledTextColor={placeholderHex}
        />
      </View>
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  subheader: {
    color: role.textSecondary,
    fontSize: 14,
    fontWeight: '400',
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    columnGap: 12,
  },
  // See issueEdit's reminderTimeField: cancels the picker's default full width
  // and pins it to the 48px standard field box so the pair lines up.
  timeField: {
    flex: 1,
    width: 'auto',
    minWidth: 0,
    height: 48,
    paddingTop: 0,
    paddingBottom: 0,
    justifyContent: 'center',
  },
}));
